/**
 * Pure MatchDay scoring math, shared by the Cloud Functions engine and the client preview.
 *
 * Lives in `packages/domain` (imported by web via `@/helpers/scoring/…` and by functions via
 * relative path). No Firebase/Firestore imports so it can run in either environment.
 * See `docs/matchday-scoring/` for the model these functions implement.
 */
import { type Country, tournamentCountriesInfo } from '../../models/country';
import type { gameResult } from '../../models/gameUpdate';
import type { ProgressionAward, TeamColumn, TeamMatrix } from '../../models/scoring';

/** All teams that belong to the active tournament. */
function tournamentTeams(): Country[] {
  return Object.values(tournamentCountriesInfo).map((t) => t.id);
}

/** A zeroed per-team column for every team in the tournament. */
export function emptyColumn(): TeamColumn {
  const col = {} as TeamColumn;
  for (const team of tournamentTeams()) {
    col[team] = 0;
  }
  return col;
}

/** A zeroed per-team matrix with one array of length `matchdayCount + 1` per team. */
export function emptyMatrix(matchdayCount: number): TeamMatrix {
  const matrix = {} as TeamMatrix;
  for (const team of tournamentTeams()) {
    matrix[team] = new Array(matchdayCount + 1).fill(0);
  }
  return matrix;
}

/**
 * Build the MatchDay Points Matrix from scored results.
 *
 * Each result contributes its (already computed) per-team points to its MatchDay column.
 * Results without a valid `matchday` are ignored by the caller-supplied validation; here we
 * simply skip anything out of range so a bad row can never corrupt the matrix shape.
 */
export function buildMPM(
  results: gameResult[],
  matchdayCount: number,
  progressionAwards: ProgressionAward[] = [],
): TeamMatrix {
  const mpm = emptyMatrix(matchdayCount);
  for (const r of results) {
    const md = r.matchday;
    if (md == null || md < 1 || md > matchdayCount) {
      continue;
    }
    if (mpm[r.countryA]) {
      mpm[r.countryA][md] += r.pointsA;
    }
    if (mpm[r.countryB]) {
      mpm[r.countryB][md] += r.pointsB;
    }
  }
  for (const award of progressionAwards) {
    const md = award.matchday;
    if (md < 1 || md > matchdayCount) continue;
    if (mpm[award.team]) {
      mpm[award.team][md] += award.points;
    }
  }
  return mpm;
}

export interface ResultsSummary {
  /** Total non-sentinel result rows inspected. */
  totalResults: number;
  /** Rows whose `matchday` equals the MatchDay being settled. */
  resultsForMatchday: number;
  /** Rows with a missing/out-of-range `matchday` (silently ignored by `buildMPM`). */
  invalidMatchdayRows: number;
  /** Distinct team codes that are not part of the tournament (silently ignored). */
  unknownTeams: string[];
  /** Sum of pointsA + pointsB across rows for the MatchDay being settled. */
  pointsAppliedForMatchday: number;
}

/**
 * Inspect results before settlement so callers can validate and report instead of
 * silently producing a zeroed matrix when a row is mistyped (wrong `matchday` field
 * name/value, or a team code that isn't in the tournament).
 */
export function summarizeResults(
  results: gameResult[],
  matchdayCount: number,
  matchday: number,
  progressionAwards: ProgressionAward[] = [],
): ResultsSummary {
  const known = new Set<string>(Object.keys(emptyColumn()));
  const unknownTeams = new Set<string>();
  let totalResults = 0;
  let resultsForMatchday = 0;
  let invalidMatchdayRows = 0;
  let pointsAppliedForMatchday = 0;

  for (const r of results) {
    totalResults += 1;
    const md = r.matchday;
    if (md == null || md < 1 || md > matchdayCount) {
      invalidMatchdayRows += 1;
    }
    for (const team of [r.countryA, r.countryB]) {
      if (team != null && !known.has(team as string)) {
        unknownTeams.add(String(team));
      }
    }
    if (md === matchday) {
      resultsForMatchday += 1;
      pointsAppliedForMatchday += (r.pointsA ?? 0) + (r.pointsB ?? 0);
    }
  }

  for (const award of progressionAwards) {
    if (award.matchday === matchday) {
      pointsAppliedForMatchday += award.points;
    }
  }

  return {
    totalResults,
    resultsForMatchday,
    invalidMatchdayRows,
    unknownTeams: [...unknownTeams],
    pointsAppliedForMatchday,
  };
}

/** Cumulative Points Matrix: `cpm[:,0] = 0`, `cpm[:,k] = cpm[:,k-1] + mpm[:,k]`. */
export function buildCPM(mpm: TeamMatrix, matchdayCount: number): TeamMatrix {
  const cpm = emptyMatrix(matchdayCount);
  for (const team of Object.keys(mpm) as Country[]) {
    const mpmRow = mpm[team];
    const cpmRow = cpm[team];
    for (let k = 1; k <= matchdayCount; k++) {
      cpmRow[k] = cpmRow[k - 1] + (mpmRow[k] ?? 0);
    }
  }
  return cpm;
}

/** Apply a two-index swap to a copy of the list (no-op when `swapPair` is null). */
export function applySwapToList(list: Country[], swapPair: [number, number] | null): Country[] {
  const out = [...list];
  if (swapPair) {
    out[swapPair[0]] = list[swapPair[1]];
    out[swapPair[1]] = list[swapPair[0]];
  }
  return out;
}

type Pick = { country: Country; mult: number };

/** Rank multipliers 10..1 applied to a 10-team list (index 0 = rank 1 = x10). */
function picksFromList(list: Country[]): Pick[] {
  return list.map((country, i) => ({ country, mult: 10 - i }));
}

/** Dot product of a pick vector with `cpm[:,hi] - cpm[:,lo]`. */
function dotDiff(picks: Pick[], cpm: TeamMatrix, hi: number, lo: number): number {
  let sum = 0;
  for (const p of picks) {
    const row = cpm[p.country];
    if (!row) continue;
    sum += p.mult * (row[hi] - row[lo]);
  }
  return sum;
}

export interface ScoreHistoryParams {
  countryList: Country[];
  swapPair: [number, number] | null;
  /** Baseline MatchDay; points through this day are forfeited. */
  mdReset: number;
  /** Swapped order applies for MatchDays strictly greater than this value. */
  swapMatchday: number;
  cpm: TeamMatrix;
  matchdayCount: number;
}

/**
 * Per-user cumulative score history U (length `matchdayCount + 1`, U[0] = 0).
 *
 * For MatchDay k (with S = swapMatchday, R = mdReset):
 *   - k <= R: U[k] = 0 (baseline; points forfeited up to and including the reset day)
 *   - otherwise: before-order points accrue over (R, min(k,S)] and, once past S,
 *     after-order points accrue over (max(S,R), k].
 *
 * This is robust to R > S (a late join / reset after the swap day), where the before
 * segment is empty and only the after vector contributes.
 */
export function computeScoreHistory(params: ScoreHistoryParams): number[] {
  const { countryList, swapPair, mdReset, swapMatchday, cpm, matchdayCount } = params;
  const U = new Array(matchdayCount + 1).fill(0);
  if (countryList.length !== 10) {
    return U;
  }

  const picksBefore = picksFromList(countryList);
  const picksAfter = picksFromList(applySwapToList(countryList, swapPair));
  const S = swapMatchday;

  for (let k = 1; k <= matchdayCount; k++) {
    if (k <= mdReset) {
      U[k] = 0;
      continue;
    }
    let val = 0;
    const beforeHi = Math.min(k, S);
    if (beforeHi > mdReset) {
      val += dotDiff(picksBefore, cpm, beforeHi, mdReset);
    }
    if (k > S) {
      val += dotDiff(picksAfter, cpm, k, Math.max(S, mdReset));
    }
    U[k] = val;
  }
  return U;
}

/** Convenience: the score a user holds at a given MatchDay (clamped to range). */
export function scoreAtMatchday(scoreHistory: number[], matchday: number): number {
  if (matchday <= 0) return scoreHistory[0] ?? 0;
  const k = Math.min(matchday, scoreHistory.length - 1);
  return scoreHistory[k] ?? 0;
}
